// Lernsimulation: geführte Übungen in der Demo der Planungsoberfläche.
// Jede Übung besteht aus Schritten mit Anleitung, Hervorhebung der Bedienelemente
// und automatischer Erfolgskontrolle (geprüft am Datenstand und an der Oberfläche).
import { useEffect, useRef, useState } from 'react';
import { useDaten } from '../daten/kontext';
import type { Stand } from '../daten/typen';
import { Icon, Knopf } from '../ui/bausteine';

// ---------------------------------------------------------------- Hilfen für Schritte
const $ = (sel: string) => document.querySelector(sel);
const mitText = (sel: string, text: string) => [...document.querySelectorAll(sel)].find(e => (e.textContent ?? '').includes(text)) ?? null;
const navAktiv = (text: string) => !!mitText('.hauptnav button.an', text);
const panelMit = (text: string) => !!mitText('.panel', text);
const kursId = (s: Stand, kennung: string) => s.kurse.find(k => k.kennung === kennung)?.id;
const einsaetzeVon = (s: Stand, kennung: string) => s.einsaetze.filter(e => e.kurs_id === kursId(s, kennung));
const offeneAbsagen = (s: Stand) => s.absagen.filter(a => !a.erledigt_am).length;
const dozent = (s: Stand, nachname: string) => s.dozenten.find(d => d.nachname === nachname);

interface Kontext { stand: Stand; start: Stand; schrittStart: Stand }

// Letzter Klick seit Beginn des aktuellen Schritts – für Schritte, deren Ergebnis schon voreingestellt sein kann
// (z. B. ist der freie Standard-Dozent vorausgewählt; der Schritt soll trotzdem eine bewusste Wahl abwarten).
let klickSeitSchritt: Element | null = null;
if (typeof document !== 'undefined') document.addEventListener('click', e => { klickSeitSchritt = e.target instanceof Element ? e.target : null; }, true);
const geklickt = (sel: string) => !!klickSeitSchritt?.closest(sel);
interface Schritt { titel: string; text: string; ziel?: () => Element | null; fertig: (k: Kontext) => boolean }
interface Uebung { id: string; titel: string; dauer: string; ziel: string; schritte: Schritt[] }

const UEBUNGEN: Uebung[] = [
  {
    id: 'orientierung', titel: 'Die Planungstafel kennenlernen', dauer: '3 Min.',
    ziel: 'Wochen wechseln, einen Dozenten über alle Kurse verfolgen, Soll und Ist eines Kurses ablesen.',
    schritte: [
      { titel: 'Planung öffnen', text: 'Klicken Sie oben in der Navigation auf „Planung“.', ziel: () => mitText('.hauptnav button', 'Planung'), fertig: () => navAktiv('Planung') },
      { titel: 'Zur nächsten Woche', text: 'Wechseln Sie mit dem Pfeil rechts neben der Kalenderwoche zu KW 41.', ziel: () => $('button[aria-label="Nächste Woche"]'),
        fertig: () => !!mitText('.woche strong', 'KW 41') },
      { titel: 'Einen Dozenten hervorheben', text: 'Wählen Sie im Feld „Dozent hervorheben …“ den Dozenten „VA · Vick“. Seine Termine leuchten in allen Kursen auf, alle anderen treten zurück.',
        ziel: () => $('select[aria-label="Dozent hervorheben"]'), fertig: () => ($('select[aria-label="Dozent hervorheben"]') as HTMLSelectElement | null)?.value !== '' && !!$('.kachel.hervor') },
      { titel: 'Soll und Ist ablesen', text: 'Klicken Sie links auf den Kurskopf „ML-26-VZ-302“. Rechts zeigt die Leiste, wie viele UE je Fachbereich geplant und freigegeben sind.',
        ziel: () => mitText('.kurs-kopf', 'ML-26-VZ-302'), fertig: () => !!mitText('.kurs-kopf.fokus', 'ML-26-VZ-302') },
      { titel: 'Hervorhebung aufheben', text: 'Stellen Sie „Dozent hervorheben“ wieder auf den leeren Eintrag zurück.', ziel: () => $('select[aria-label="Dozent hervorheben"]'),
        fertig: () => ($('select[aria-label="Dozent hervorheben"]') as HTMLSelectElement | null)?.value === '' },
    ],
  },
  {
    id: 'termin', titel: 'Einen Termin planen und freigeben', dauer: '4 Min.',
    ziel: 'Einen Unterrichtstermin anlegen, dabei nur verfügbare Dozenten wählen und ihn für Moodle und die Dozenten-App freigeben.',
    schritte: [
      { titel: 'Woche 42 aufrufen', text: 'Gehen Sie in der Planung zu KW 42 (12.10. bis 16.10.). Dort beginnt der Kurs ML-26-VZ-304 in Weiterstadt.',
        ziel: () => $('button[aria-label="Nächste Woche"]'), fertig: () => navAktiv('Planung') && !!mitText('.woche strong', 'KW 42') },
      { titel: 'Freies Feld antippen', text: 'Klicken Sie in der Zeile „ML-26-VZ-304“ auf ein freies Feld mit dem Plus, zum Beispiel Montag Vormittag.',
        ziel: () => { const kopf = mitText('.kurs-kopf', 'ML-26-VZ-304'); let el = kopf?.nextElementSibling; while (el && !el.querySelector('.kachel.leer')) el = el.nextElementSibling; return el?.querySelector('.kachel.leer') ?? null; },
        fertig: () => panelMit('ML-26-VZ-304') },
      { titel: 'Thema und Dozent wählen', text: 'Das System schlägt das nächste offene Thema und – wenn er frei ist – den Standard-Dozenten (Stern) vor. Klicken Sie einen Dozenten aus der Liste „verfügbar“ an. Wer nicht kann, steht mit Grund unter „Nicht verfügbar“ und lässt sich nicht wählen.',
        ziel: () => $('.dozentenwahl'), fertig: () => geklickt('.dozent-option:not(.offen):not(.gesperrt)') && !!$('.dozent-option.an:not(.offen):not(.gesperrt)') },
      { titel: 'Als Entwurf speichern', text: 'Klicken Sie auf „Speichern“. Der Termin erscheint gestrichelt als Entwurf – sichtbar nur für die Verwaltung.',
        ziel: () => mitText('.panel .knopf', 'Speichern'),
        fertig: k => einsaetzeVon(k.stand, 'ML-26-VZ-304').length > einsaetzeVon(k.start, 'ML-26-VZ-304').length && !$('.panel') },
      { titel: 'Woche freigeben', text: 'Klicken Sie oben rechts auf „Woche freigeben“. Ab jetzt sehen die Teilnehmenden den Termin in Moodle und der Dozent in seiner App.',
        ziel: () => mitText('.werkzeugleiste .knopf', 'Woche freigeben'),
        fertig: k => einsaetzeVon(k.stand, 'ML-26-VZ-304').some(e => e.status === 'freigegeben') },
    ],
  },
  {
    id: 'absage', titel: 'Eine Absage bearbeiten', dauer: '3 Min.',
    ziel: 'Auf die Absage einer Dozentin reagieren und eine Vertretung einplanen.',
    schritte: [
      { titel: 'Absagen öffnen', text: 'Die rote Zahl an „Absagen“ zeigt offene Absagen. Öffnen Sie die Seite.', ziel: () => mitText('.hauptnav button', 'Absagen'), fertig: () => navAktiv('Absagen') },
      { titel: 'Ersatz einplanen', text: 'Fr. Kerner hat den 06.10. abgesagt. Die Karte zeigt bereits, wer als Vertretung infrage kommt. Klicken Sie auf „Ersatz einplanen“.',
        ziel: () => mitText('.karte.absage .knopf', 'Ersatz einplanen'), fertig: () => !!$('.panel .dozentenwahl') },
      { titel: 'Vertretung wählen und speichern', text: 'Wählen Sie eine verfügbare Vertretung und klicken Sie auf „Speichern“. Die Absage erledigt sich damit von selbst.',
        ziel: () => $('.dozentenwahl'), fertig: k => offeneAbsagen(k.stand) < offeneAbsagen(k.start) },
    ],
  },
  {
    id: 'abwesenheit', titel: 'Abwesenheit für einen Dozenten ohne App', dauer: '2 Min.',
    ziel: 'Die Rückmeldung eines Dozenten (z. B. aus dem Word-Formular) im Kalender eintragen.',
    schritte: [
      { titel: 'Dozenten öffnen', text: 'Öffnen Sie in der Navigation „Dozenten“.', ziel: () => mitText('.hauptnav button', 'Dozenten'), fertig: () => navAktiv('Dozenten') },
      { titel: 'Hr. Dorn auswählen', text: 'Hr. Felix Dorn nutzt die App noch nicht (Spalte „App“ zeigt „Einladen“). Klicken Sie auf seinen Namen.',
        ziel: () => mitText('.tabelle button', 'Dorn'), fertig: () => panelMit('Felix Dorn') },
      { titel: 'Tag markieren', text: 'Wählen Sie „Ganzer Tag“, tragen Sie als Grund „Urlaub“ ein und tippen Sie im Kalender auf einen Tag, an dem Hr. Dorn nicht kann.',
        ziel: () => $('.monat'), fertig: k => { const d = dozent(k.stand, 'Dorn'); return !!d && k.stand.abwesenheiten.filter(a => a.dozent_id === d.id).length > k.start.abwesenheiten.filter(a => a.dozent_id === d.id).length; } },
    ],
  },
  {
    id: 'kurs', titel: 'Neuen Kurs anlegen und vorplanen', dauer: '4 Min.',
    ziel: 'Einen Kurs aus der Vorlage anlegen und vom System einen Planvorschlag erzeugen lassen.',
    schritte: [
      { titel: 'Kurse öffnen', text: 'Öffnen Sie „Kurse“ und klicken Sie auf „Kurs anlegen“.', ziel: () => mitText('.knopf', 'Kurs anlegen') ?? mitText('.hauptnav button', 'Kurse'), fertig: () => panelMit('Der Lehrplan wird aus der Vorlage übernommen') },
      { titel: 'Kurs anlegen', text: 'Geben Sie eine Kennung ein (z. B. ML-27-VZ-401). Prüfen Sie den Hinweis zu Unterrichtstagen und UE und klicken Sie auf „Kurs anlegen“.',
        ziel: () => $('input[placeholder="z. B. ML-99-VZ-236"]'), fertig: k => k.stand.kurse.length > k.start.kurse.length },
      { titel: 'Zur Planung', text: 'Schließen Sie das Fenster und klicken Sie beim neuen Kurs auf „Planen“.',
        ziel: () => { const neu = [...document.querySelectorAll('.kurs-karte')].pop(); return neu?.querySelector('.knopf') ?? null; }, fertig: () => navAktiv('Planung') },
      { titel: 'Planvorschlag erzeugen', text: 'Klicken Sie auf „Vorschlag“ und bestätigen Sie. Das System füllt die freien Zeitfenster der Woche mit Entwürfen – zuerst mit dem Standard-Dozenten, sonst mit dem am wenigsten ausgelasteten freien Dozenten.',
        ziel: () => mitText('.werkzeugleiste .knopf', 'Vorschlag'),
        fertig: k => { const neu = k.stand.kurse.find(x => !k.start.kurse.some(y => y.id === x.id)); return !!neu && k.stand.einsaetze.some(e => e.kurs_id === neu.id); } },
    ],
  },
  {
    id: 'stempel', titel: 'Schnell planen mit dem Stempel (Touch)', dauer: '3 Min.',
    ziel: 'Auf dem großen Touch-Bildschirm mehrere Termine nacheinander antippen.',
    schritte: [
      { titel: 'Touch-Modus einschalten', text: 'Schalten Sie oben rechts „Touch“ ein. Alle Bedienelemente werden größer.', ziel: () => mitText('.kopf-rechts .knopf', 'Touch'),
        fertig: () => document.documentElement.dataset.touch === '1' },
      { titel: 'Zur Planung, KW 42', text: 'Öffnen Sie die Planung und wechseln Sie zu KW 42.', ziel: () => $('button[aria-label="Nächste Woche"]'),
        fertig: () => navAktiv('Planung') && !!mitText('.woche strong', 'KW 42') },
      { titel: 'Stempel aufnehmen', text: 'Wählen Sie rechts in der Soll-Ist-Leiste den Kurs ML-26-VZ-304 und tippen Sie beim Fachbereich „Rechtskunde“ auf das Stempel-Symbol.',
        ziel: () => $('.leiste-kopf select'), fertig: () => !!mitText('.stempel-hinweis', 'ML-26-VZ-304') },
      { titel: 'Zwei Felder stempeln', text: 'Tippen Sie nacheinander auf zwei freie Felder von ML-26-VZ-304. Jedes Antippen legt einen Entwurf mit dem nächsten offenen Thema an.',
        ziel: () => $('.kachel.leer.stempelbar'), fertig: k => einsaetzeVon(k.stand, 'ML-26-VZ-304').length >= einsaetzeVon(k.schrittStart, 'ML-26-VZ-304').length + 2 },
      { titel: 'Stempel beenden', text: 'Beenden Sie den Stempel über „Beenden“ und schalten Sie den Touch-Modus wieder aus.', ziel: () => mitText('.stempel-hinweis .knopf', 'Beenden'),
        fertig: () => !$('.stempel-hinweis') && document.documentElement.dataset.touch !== '1' },
    ],
  },
  {
    id: 'moodle', titel: 'Stundenplan in Moodle einbinden', dauer: '2 Min.',
    ziel: 'Den Code für den Live-Stundenplan eines Kurses holen.',
    schritte: [
      { titel: 'Kurs öffnen', text: 'Öffnen Sie „Kurse“ und beim Kurs ML-26-VZ-301 „Einstellungen und Lehrplan“.',
        ziel: () => { const k = mitText('.kurs-karte', 'ML-26-VZ-301'); return k ? mitText('.kurs-karte .knopf', 'Einstellungen') : mitText('.hauptnav button', 'Kurse'); },
        fertig: () => panelMit('ML-26-VZ-301') && !!$('.reiter') },
      { titel: 'Reiter „Moodle“', text: 'Wechseln Sie auf den Reiter „Moodle“. Unten sehen Sie eine Vorschau, so wie die Teilnehmenden den Stundenplan sehen.',
        ziel: () => mitText('.panel .reiter button', 'Moodle'), fertig: () => !!$('#moodle-code') },
      { titel: 'Code kopieren', text: 'Klicken Sie auf „Code kopieren“. Den Code fügen Sie in Moodle in einen Textbereich ein (HTML-Ansicht).',
        ziel: () => mitText('.panel .knopf', 'Code kopieren'), fertig: () => Date.now() - ((window as any).__mmKopiert ?? 0) < 60000 },
    ],
  },
];

const SPEICHER = 'mm-simulation-erledigt';
function erledigtLesen(): string[] { try { return JSON.parse(localStorage.getItem(SPEICHER) ?? '[]'); } catch { return []; } }
function erledigtSchreiben(x: string[]) { try { localStorage.setItem(SPEICHER, JSON.stringify(x)); } catch { /* egal */ } }

export function Simulation({ offen, schliessen }: { offen: boolean; schliessen: () => void }) {
  const { stand, quelle, ausfuehren } = useDaten();
  const [aktiv, setAktiv] = useState<{ uebung: Uebung; schritt: number } | null>(null);
  const [erledigt, setErledigt] = useState<string[]>(erledigtLesen);
  const [zuruecksetzen, setZuruecksetzen] = useState(true);
  const [zeigen, setZeigen] = useState(true);
  const start = useRef<Stand>(stand);
  const schrittStart = useRef<Stand>(stand);
  const standRef = useRef(stand); standRef.current = stand;
  const [, tick] = useState(0);
  const [rechts, setRechts] = useState(false);
  const karteRef = useRef<HTMLElement>(null);

  // Fortschritt prüfen und Ziel hervorheben
  useEffect(() => {
    if (!aktiv) return;
    const t = setInterval(() => {
      const s = aktiv.uebung.schritte[aktiv.schritt];
      document.querySelectorAll('.sim-ziel').forEach(e => e.classList.remove('sim-ziel'));
      if (!s) return;
      const ziel = s.ziel?.() ?? null;
      if (zeigen) ziel?.classList.add('sim-ziel');
      // Karte auf die andere Seite schieben, wenn sie das Ziel verdeckt
      const karte = karteRef.current;
      if (ziel && karte) {
        const a = ziel.getBoundingClientRect(), k = karte.getBoundingClientRect();
        const deckt = a.left < k.right && k.left < a.right && a.top < k.bottom && k.top < a.bottom;
        if (deckt) setRechts(r => !r);
      }
      if (s.fertig({ stand: standRef.current, start: start.current, schrittStart: schrittStart.current })) {
        schrittStart.current = standRef.current;
        klickSeitSchritt = null;
        if (aktiv.schritt + 1 >= aktiv.uebung.schritte.length) {
          const n = [...new Set([...erledigt, aktiv.uebung.id])]; setErledigt(n); erledigtSchreiben(n);
          setAktiv({ ...aktiv, schritt: aktiv.uebung.schritte.length });
        } else setAktiv({ ...aktiv, schritt: aktiv.schritt + 1 });
      }
      tick(x => x + 1);
    }, 400);
    return () => { clearInterval(t); document.querySelectorAll('.sim-ziel').forEach(e => e.classList.remove('sim-ziel')); };
  }, [aktiv, zeigen, erledigt]);

  async function beginnen(u: Uebung) {
    if (zuruecksetzen && quelle.zuruecksetzen) await ausfuehren(() => quelle.zuruecksetzen!());
    // Stand nach dem Zurücksetzen als Ausgangslage merken
    const neu = await quelle.laden();
    start.current = neu; schrittStart.current = neu;
    setZeigen(true);
    setAktiv({ uebung: u, schritt: 0 });
  }

  if (!offen && !aktiv) return null;

  if (aktiv) {
    const fertig = aktiv.schritt >= aktiv.uebung.schritte.length;
    const s = aktiv.uebung.schritte[aktiv.schritt];
    return (
      <aside ref={karteRef} className={`sim ${rechts ? 'rechts' : ''}`} aria-live="polite" aria-label="Übung">
        <div className="sim-kopf">
          <span className="sim-eyebrow">Übung · {aktiv.uebung.titel}</span>
          <Knopf art="leise" icon="x" aria-label="Übung beenden" onClick={() => { setAktiv(null); }} />
        </div>
        {fertig ? (
          <div className="sim-fertig">
            <Icon name="haken" groesse={28} />
            <strong>Geschafft!</strong>
            <p>{aktiv.uebung.ziel}</p>
            <div className="knopfleiste"><Knopf art="primaer" onClick={() => setAktiv(null)}>Zur Übersicht</Knopf></div>
          </div>
        ) : <>
          <div className="sim-fortschritt" aria-label={`Schritt ${aktiv.schritt + 1} von ${aktiv.uebung.schritte.length}`}>
            {aktiv.uebung.schritte.map((_, i) => <span key={i} className={i < aktiv.schritt ? 'fertig' : i === aktiv.schritt ? 'jetzt' : ''} />)}
          </div>
          <strong className="sim-titel">{aktiv.schritt + 1}. {s.titel}</strong>
          <p>{s.text}</p>
          <div className="knopfleiste">
            {s.ziel && <Knopf art="leise" icon="hand" onClick={() => { setZeigen(true); s.ziel?.()?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }}>Zeig mir, wo</Knopf>}
            <Knopf art="leise" onClick={() => { schrittStart.current = standRef.current; setAktiv({ ...aktiv, schritt: aktiv.schritt + 1 >= aktiv.uebung.schritte.length ? aktiv.uebung.schritte.length : aktiv.schritt + 1 }); }}>Überspringen</Knopf>
          </div>
          <p className="leise klein">Der nächste Schritt erscheint automatisch, sobald Sie diesen erledigt haben.</p>
        </>}
      </aside>
    );
  }

  return (
    <aside className="sim sim-liste" aria-label="Übungen">
      <div className="sim-kopf">
        <span className="sim-eyebrow">Lernsimulation · {erledigt.length} von {UEBUNGEN.length} erledigt</span>
        <Knopf art="leise" icon="x" aria-label="Schließen" onClick={schliessen} />
      </div>
      <p className="klein">Üben Sie die wichtigsten Abläufe an Beispieldaten. Die Simulation zeigt Ihnen jeden Schritt und erkennt selbst, wann er erledigt ist.</p>
      <ul>
        {UEBUNGEN.map(u => (
          <li key={u.id}>
            <button type="button" className="sim-uebung" onClick={() => beginnen(u)}>
              <span className={`sim-status ${erledigt.includes(u.id) ? 'fertig' : ''}`}>{erledigt.includes(u.id) ? <Icon name="haken" groesse={14} /> : null}</span>
              <span className="sim-uebung-text"><strong>{u.titel}</strong><small>{u.ziel}</small></span>
              <span className="leise klein">{u.dauer}</span>
            </button>
          </li>
        ))}
      </ul>
      <label className="check klein"><input id="sim-zuruecksetzen" type="checkbox" checked={zuruecksetzen} onChange={e => setZuruecksetzen(e.target.checked)} /> Beispieldaten vor jeder Übung zurücksetzen (empfohlen)</label>
    </aside>
  );
}
